import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { PERSON_TYPES, isPlaceholderEntityName } from "score-common";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useAccountApi } from "../api/accountApi";
import { useShowRides } from "../hooks/useShowRides";
import { useSortedRows } from "../hooks/useSortedRows";
import { useNextRides } from "../hooks/useNextRides";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import NextRideCell from "../components/NextRideCell";
import RideHistoryDrawer from "../components/RideHistoryDrawer";

// #507: replace the People placeholder with a real search + detail screen.
// #592: FVD types nearly everyone as personType=RIDER (owners/trainers/
// coaches included), and judges/scribes aren't score-persons at all — judges
// live as NAMES on class.judgeAssignments, scribes are account members with
// the scribe role. So each tab now has its own data source instead of all
// three sharing listPeople(personType):
//   - Riders: still listPeople(RIDER), but further filtered to people who
//     actually appear as a ride's riderId in the active show.
//   - Judges: distinct names collected from every class's judgeAssignments.
//   - Scribes: org members holding the score:score_scribe role (mirrors
//     ClassManager.jsx's scribe load).
// Only the Riders tab keeps the rich sortable table + RideHistoryDrawer;
// Judges/Scribes render a simple searchable name list.
const SCRIBE_ROLE = "score:score_scribe";

const TABS = [
  { value: PERSON_TYPES.RIDER, labelKey: "people.tabs.riders" },
  { value: PERSON_TYPES.JUDGE, labelKey: "people.tabs.judges" },
  { value: PERSON_TYPES.SCRIBE, labelKey: "people.tabs.scribes" },
];

function personName(person) {
  return [person.firstName, person.lastName].filter(Boolean).join(" ") || person.id;
}

export default function PeopleManager() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.people"));
  const scoreApi = useScoreApi();
  const accountApi = useAccountApi();
  const { activeOrgId } = useActiveOrg();
  const { activeShowId } = useActiveShow();
  // #558 — each rider row shows their next ride in the active show.
  const { nextByRider } = useNextRides(activeShowId);
  // #592/#1906 — the active show's rides, used to derive who actually rides.
  // useShowRides is always progressive: the most recent day's rides arrive
  // first, the rest merge in behind them (`loadingMore`). riderIds below is
  // memoized on `rides`, so the Riders tab simply grows as more days land —
  // never shows a wrong or stale set, just a smaller one until it catches up.
  const { rides, loadingMore: loadingMoreRides } = useShowRides(activeShowId);

  const [personType, setPersonType] = useState(PERSON_TYPES.RIDER);
  const [people, setPeople] = useState([]); // riders: raw RIDER-typed person rows
  const [judgeNames, setJudgeNames] = useState([]); // judges: distinct names off classes
  const [scribes, setScribes] = useState([]); // scribes: scribe-roled org members
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Riders: all RIDER-typed people (further filtered below to those who
  // actually appear on a ride).
  useEffect(() => {
    if (personType !== PERSON_TYPES.RIDER) return undefined;
    // #776 — org context (X-Org-Id) isn't wired into scoreApi until
    // activeOrgId resolves. Fetching before then returns an empty/unscoped
    // list; stay on the skeleton (don't flip loading off) until the
    // context-ready run.
    if (!activeOrgId) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const list = unwrap(await scoreApi.people.listPeople(PERSON_TYPES.RIDER));
        if (!cancelled) setPeople(list);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personType, scoreApi, activeOrgId]);

  // Judges: FVD has no judge "person" record — judges live as names on each
  // class's judgeAssignments ({ position: [judgeName, ...] }). Collect the
  // distinct set across the active show's classes.
  useEffect(() => {
    if (personType !== PERSON_TYPES.JUDGE) return undefined;
    // #776 — stay on the skeleton until show context resolves rather than
    // flipping loading off on a pre-context empty result.
    if (!activeShowId) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const classes = unwrap(await scoreApi.classes.listClasses(activeShowId));
        const names = new Set();
        for (const c of classes) {
          for (const list of Object.values(c.judgeAssignments || {})) {
            for (const name of list || []) {
              if (name && !isPlaceholderEntityName(name)) names.add(name);
            }
          }
        }
        if (!cancelled) setJudgeNames([...names].sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personType, activeShowId, scoreApi]);

  // Scribes: not score-persons at all — account members holding the scribe
  // role for the active org (mirrors ClassManager.jsx's scribe load).
  useEffect(() => {
    if (personType !== PERSON_TYPES.SCRIBE) return undefined;
    // #776 — stay on the skeleton until org context resolves rather than
    // flipping loading off on a pre-context empty result.
    if (!activeOrgId) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await accountApi.org.listOrganizationMembers(activeOrgId);
        const members =
          res.data?.status === "success" && Array.isArray(res.data.data?.members)
            ? res.data.data.members
            : [];
        if (!cancelled) setScribes(members.filter((m) => (m.roles || []).includes(SCRIBE_ROLE)));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personType, activeOrgId, accountApi]);

  // #592 — the set of person ids that actually ride in the active show, so
  // the Riders tab excludes owners/trainers/coaches FVD also types RIDER.
  const riderIds = useMemo(() => new Set(rides.map((r) => r.riderId).filter(Boolean)), [rides]);

  const filteredRiders = useMemo(() => {
    const q = search.trim().toLowerCase();
    // #585 — hide FVD placeholder rows ("***Break***") already seeded before
    // the reconcile-side filter landed.
    const base = people.filter(
      (p) =>
        riderIds.has(p.id) &&
        !isPlaceholderEntityName(p.firstName) &&
        !isPlaceholderEntityName(p.lastName) &&
        !isPlaceholderEntityName(personName(p)),
    );
    if (!q) return base;
    return base.filter((p) => personName(p).toLowerCase().includes(q));
  }, [people, riderIds, search]);

  // #557 — sortable columns over the search-filtered rider rows.
  const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(
    filteredRiders,
    { name: (p) => personName(p), usefId: (p) => p.usefId },
    "name",
  );

  const filteredJudges = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return judgeNames;
    return judgeNames.filter((name) => name.toLowerCase().includes(q));
  }, [judgeNames, search]);

  const filteredScribes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scribes;
    return scribes.filter((m) => userDisplayName(m).toLowerCase().includes(q));
  }, [scribes, search]);

  const isRiderTab = personType === PERSON_TYPES.RIDER;
  const isJudgeTab = personType === PERSON_TYPES.JUDGE;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("people.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("people.loadError")}
        </Alert>
      )}

      <Tabs
        value={personType}
        onChange={(_e, value) => {
          setPersonType(value);
          setSelected(null);
        }}
        sx={{ mb: 2 }}
      >
        {TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={t(tab.labelKey)} />
        ))}
      </Tabs>

      <TextField
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 360 }}
        placeholder={t("people.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <TableSkeleton rows={5} columns={isRiderTab ? 3 : 1} />
      ) : isRiderTab ? (
        sorted.length === 0 ? (
          <Typography color="text.secondary">{t("people.empty")}</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortKey === "name" ? sortDir : false}>
                    <TableSortLabel
                      active={sortKey === "name"}
                      direction={sortKey === "name" ? sortDir : "asc"}
                      onClick={() => toggleSort("name")}
                    >
                      {t("people.columns.name")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortKey === "usefId" ? sortDir : false}>
                    <TableSortLabel
                      active={sortKey === "usefId"}
                      direction={sortKey === "usefId" ? sortDir : "asc"}
                      onClick={() => toggleSort("usefId")}
                    >
                      {t("people.columns.usefId")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t("people.columns.nextRide")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((person) => (
                  <TableRow
                    key={person.id}
                    hover
                    onClick={() => setSelected(person)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{personName(person)}</TableCell>
                    <TableCell>{person.usefId || "-"}</TableCell>
                    <TableCell>
                      <NextRideCell
                        nextRide={nextByRider[person.id]}
                        ringLabel={t("nextRide.ring")}
                        emptyLabel={t("nextRide.none")}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : isJudgeTab ? (
        filteredJudges.length === 0 ? (
          <Typography color="text.secondary">{t("people.empty")}</Typography>
        ) : (
          <List>
            {filteredJudges.map((name) => (
              <ListItem key={name} divider>
                <ListItemText primary={name} primaryTypographyProps={{ color: "text.primary" }} />
              </ListItem>
            ))}
          </List>
        )
      ) : filteredScribes.length === 0 ? (
        <Typography color="text.secondary">{t("people.empty")}</Typography>
      ) : (
        <List>
          {filteredScribes.map((m) => (
            <ListItem key={m.userId || m.email} divider>
              <ListItemText
                primary={userDisplayName(m)}
                primaryTypographyProps={{ color: "text.primary" }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* #1906 — the Riders tab derives from useShowRides's progressive
          rides, so a later show day can still be filling in behind the
          first paint; the list only ever grows, never shows stale/wrong
          rows, so this is purely informational. */}
      {isRiderTab && loadingMoreRides && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {t("people.loadingMoreRiders")}
        </Typography>
      )}

      {isRiderTab && (
        <RideHistoryDrawer
          open={!!selected}
          onClose={() => setSelected(null)}
          title={selected ? personName(selected) : ""}
          infoItems={
            selected
              ? [
                  { label: t("people.columns.usefId"), value: selected.usefId },
                  { label: t("nav.people"), value: selected.personType },
                ]
              : []
          }
          filterKey="riderId"
          subjectId={selected?.id}
        />
      )}
    </Box>
  );
}
